// Weekly scoring and the week win bonus.
//
// Season 0 exposed the failure mode this exists to fix: once somebody fell
// behind, the competition stopped being a reason to play and became a reason to
// quit. A single season-long total means a slow first week is permanent, and
// nobody grinds out four more weeks of a race they have already lost.
//
// So every week has its own winner and its own bonus. You can lose week one,
// win week two, and be back in it. The season total still crowns the champion,
// which keeps the whole thing coherent, but the week is the unit that keeps
// somebody showing up.
//
// Bonuses are DERIVED, never stored. A stored award has to be granted by
// something, at some moment, exactly once, and that pipeline is where the bugs
// live: a drop that reveals late, a cron that runs twice, a week recomputed
// after a correction. Deriving from answers means the board is always a pure
// function of what actually happened.

import { leaderboardFromAnswers } from "./leaderboard";
import type { AnswerRow, RankedLeaderboardEntry } from "./types";

export const DEFAULT_WEEK_WIN_BONUS = 3;

export interface WeeklyDrop {
  dropId: string;
  // When the drop revealed. A drop is scored in the week it was PLAYED, not the
  // week it was scheduled for, because a drop that slips past midnight on a
  // Sunday belongs to the week the crew actually played it in.
  revealedAt: string | null;
}

// A season's start date as an instant, whatever shape it arrives in.
//
// Supabase's REST API hands back a `date` column as "2026-08-21", but a direct
// Postgres client hands back a full timestamp. Concatenating "T00:00:00Z" onto
// the second one produces garbage that parses as NaN, and the silent result of
// that is every drop landing in week 1 with nobody ever winning a week. Worth
// being tolerant about rather than trusting the caller.
function startOfSeasonMs(startsOn: string): number {
  if (!startsOn) return NaN;
  const dateOnly = startsOn.length > 10 ? startsOn.slice(0, 10) : startsOn;
  return Date.parse(`${dateOnly}T00:00:00Z`);
}

// Which week of the season a moment falls in, 1-based.
export function weekIndexFor(startsOn: string, whenIso: string): number {
  const start = startOfSeasonMs(startsOn);
  const when = Date.parse(whenIso);
  if (!Number.isFinite(start) || !Number.isFinite(when)) return 1;
  const days = Math.floor((when - start) / 86400000);
  return Math.max(1, Math.floor(days / 7) + 1);
}

export interface WeekBlock {
  week: number;
  dropIds: string[];
  // A week is settled once it cannot change: every drop it holds has revealed
  // AND the week itself is over. An unsettled week shows a leader, never a
  // winner, because handing somebody a bonus that can still evaporate is worse
  // than making them wait for it.
  settled: boolean;
}

export interface WeekInput {
  startsOn: string;
  endsOn: string;
  drops: WeeklyDrop[];
  nowMs: number;
}

export function weekBlocks(input: WeekInput): WeekBlock[] {
  const byWeek = new Map<number, string[]>();
  for (const d of input.drops) {
    if (!d.revealedAt) continue; // unrevealed drops score nothing yet
    const w = weekIndexFor(input.startsOn, d.revealedAt);
    byWeek.set(w, [...(byWeek.get(w) ?? []), d.dropId]);
  }

  const seasonStart = startOfSeasonMs(input.startsOn);
  const seasonEnd = startOfSeasonMs(input.endsOn) + 86400000 - 1;

  return [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([week, dropIds]) => {
      // The moment this week stops being able to take new drops.
      const weekEnd = seasonStart + week * 7 * 86400000;
      const closed = input.nowMs >= Math.min(weekEnd, seasonEnd + 1000);
      return { week, dropIds, settled: closed };
    });
}

export interface WeekResult {
  week: number;
  settled: boolean;
  standings: RankedLeaderboardEntry[];
  // Everyone tied at the top. A shared week is shared, not broken by a coin
  // flip: the ranking already breaks ties on speed, so anyone still level after
  // that genuinely tied and both should get the bonus.
  winners: string[];
}

export function weekResults(
  blocks: WeekBlock[],
  answersByDrop: Map<string, AnswerRow[]>
): WeekResult[] {
  return blocks.map((b) => {
    const rows = b.dropIds.flatMap((id) => answersByDrop.get(id) ?? []);
    const standings = leaderboardFromAnswers(rows);
    // Nobody scored, nobody won. A week where every answer was wrong should not
    // hand a bonus to whoever was fastest at being wrong.
    const top = standings.filter((s) => s.rank === 1 && s.points > 0);
    return {
      week: b.week,
      settled: b.settled,
      standings,
      winners: b.settled ? top.map((s) => s.user_id) : [],
    };
  });
}

// Bonus points per person, from settled weeks only.
export function weekBonuses(results: WeekResult[], bonus: number): Map<string, number> {
  const out = new Map<string, number>();
  for (const r of results) {
    for (const w of r.winners) out.set(w, (out.get(w) ?? 0) + bonus);
  }
  return out;
}

export interface SeasonEntry extends RankedLeaderboardEntry {
  // Points from answers alone, before any week bonus. Kept so the board can
  // show where a total came from instead of an unexplained number.
  base_points: number;
  bonus_points: number;
  weeks_won: number;
}

// The season board: answer points plus week bonuses, re-ranked together.
export function seasonStandings(
  answerRows: AnswerRow[],
  results: WeekResult[],
  bonus: number
): SeasonEntry[] {
  const bonuses = weekBonuses(results, bonus);
  const wins = new Map<string, number>();
  for (const r of results) for (const w of r.winners) wins.set(w, (wins.get(w) ?? 0) + 1);

  // Re-rank with the bonus folded in, so the speed tiebreak still applies to the
  // real total rather than to the pre-bonus one.
  const base = leaderboardFromAnswers(answerRows);
  const withBonus = base.map((e) => ({
    ...e,
    points: e.points + (bonuses.get(e.user_id) ?? 0),
    base_points: e.points,
    bonus_points: bonuses.get(e.user_id) ?? 0,
    weeks_won: wins.get(e.user_id) ?? 0,
  }));

  // leaderboardFromAnswers cannot be reused here: it aggregates rows, and these
  // are already aggregated. Rank them directly with the same comparison.
  const sorted = [...withBonus].sort((a, b) => {
    if (a.points !== b.points) return b.points - a.points;
    const aAvg = a.avg_correct_response_ms, bAvg = b.avg_correct_response_ms;
    if (aAvg == null && bAvg == null) return 0;
    if (aAvg == null) return 1;
    if (bAvg == null) return -1;
    if (aAvg !== bAvg) return aAvg - bAvg;
    return a.total_correct_response_ms - b.total_correct_response_ms;
  });

  const ranked: SeasonEntry[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    const tied = prev
      && prev.points === cur.points
      && prev.avg_correct_response_ms === cur.avg_correct_response_ms
      && prev.total_correct_response_ms === cur.total_correct_response_ms;
    ranked.push({ ...cur, rank: i === 0 ? 1 : tied ? ranked[i - 1].rank : i + 1 });
  }
  return ranked;
}

// What to tell somebody about the week in one line.
export function describeWeek(r: WeekResult | undefined, nameOf: (id: string) => string): string {
  if (!r || r.standings.length === 0) return "Nothing played this week yet.";
  const top = r.standings.filter((s) => s.rank === 1 && s.points > 0);
  if (top.length === 0) return "Still anybody's week.";
  const names = top.map((s) => nameOf(s.user_id));
  const who = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return r.settled
    ? `${who} took the week.`
    : `${who} ${names.length === 1 ? "is" : "are"} ahead this week.`;
}
